<template>
  <v-flex>
    <div v-if="orderLines.length" class="mb-2">
      <div v-for="(orderLine, i) in orderLines" :key="i">
        <order-line
          v-if="!orderLine.parentOrderLine"
          :orderLine="orderLine"
          :orderLines="orderLines"
          :isPaid="isPaid"
          :firstOrderLine="i === orderLines.indexOf(orderLines.filter(o => !o.parentOrderLine)[0])"
          :currencies="currencies"
        />
      </div>
    </div>
    <v-alert :value="someFreeProduct && !isPaid" text color="info-text" class="info mx-3" transition="scale-transition" mode="in-out">
      <v-layout align-start>
        <v-icon class="mr-2 mt-1">mdi-alert-circle-outline</v-icon>
        <span class="free-price-alert">{{ $t('activity_reservation.warning.some_product_included') }}</span>
      </v-layout>
    </v-alert>
    <v-divider class="mb-4 mx-2" />
    <!-- SUBTOTAL -->
    <v-flex pb-3>
      <v-layout column align-end justify-center mr-3>
        <span v-if="showDisplayCurrency" class="color-text caption fix-height">
          {{ getPriceString({ amountAndCurrency: amountAndCurrency, displayCurrency: displayCurrency }) }}
        </span>
      </v-layout>
      <v-layout v-if="amountAndCurrency.amount" mx-3 align-end justify-center class="bold-text">
        <span class="totals-text primary--text text--darken-1 bold-text">{{$t('activity_reservation.subtotal')}}</span>
        <v-spacer/>
        <span class="amount-text color-text">
          {{ getPriceString({ amountAndCurrency: amountAndCurrency }).split(' ')[0] }}
          <span class="currency-text color-text">
            {{ getPriceString({ amountAndCurrency: amountAndCurrency }).split(' ')[1] }}
          </span>
        </span>
      </v-layout>
    </v-flex>
    <!-- SERVICE CHARGE -->
    <v-flex v-for="(charge, index) in serviceChargesToShow" :key="index" class="grey lighten-5" py-3>
      <v-layout column align-end justify-center mr-3>
        <span 
          v-if="showDisplayCurrency" 
          class="caption color-text fix-height"
          style="font-weigth: 400"
        >
          {{ getPriceString({ amountAndCurrency: {amount:charge.amount, currency:charge.currency}, displayCurrency }) }}
        </span>
      </v-layout>
      <v-layout align-center mx-3>
        <span class="service-charge-text">{{$t('activity_reservation.service_charge', {serviceCharge: charge.value})}}</span>
        <v-spacer/>
        <span class="color-text bold-text amount-text">
          {{ getPriceString({ amountAndCurrency: {amount:charge.amount, currency:charge.currency} }).split(' ')[0] }}
          <span class="currency-text color-text bold-text">
            {{ getPriceString({ amountAndCurrency: {amount:charge.amount, currency:charge.currency} }).split(' ')[1] }}
          </span>
        </span>
      </v-layout>
    </v-flex>
    <!-- TOTAL -->
    <v-flex v-if="amountAndCurrency && amountAndCurrency.amount" class="primary white--text" style="font-weight: 700" py-4>
      <v-layout column align-end mr-3 class="fix-height">
        <span v-if="showDisplayCurrency" class="caption">
          {{ getPriceString({ amountAndCurrency: totalPrice, displayCurrency: displayCurrency }) }}
        </span>
      </v-layout>
      <v-layout v-if="amountAndCurrency.amount" align-end mx-3>
        <span class="totals-text">{{$t('activity_reservation.total')}}</span>
        <v-spacer/>
        <span class="amount-text">
          {{ getPriceString({ amountAndCurrency: totalPrice }).split(' ')[0] }}
          <span class="currency-text">
            {{ getPriceString({ amountAndCurrency: totalPrice }).split(' ')[1] }}
          </span>
        </span>
      </v-layout>
    </v-flex>
  </v-flex>
</template>

<script>
import { mapGetters } from 'vuex'

import { orderLineItemEntities, orderLineTypes } from '@/config'

import moneyMixins from '@/mixins/MoneyFormat'

export default {
  props: {
    orderLines: Array,
    totalByCurrencies: Array,
    isPaid: Boolean,
    currencies: Array,
  },
  mixins: [
    moneyMixins,
  ],
  components: {
    OrderLine: () => import('@/components/view-components/catalog/OrderLine.vue'),
  },
  computed: {
    ...mapGetters('me', ['getPriceString', 'showDisplayCurrency', 'displayCurrency']),
    amountAndCurrency () {
      return this.totalByCurrencies[0]
    },
    someFreeProduct () {
      const { PRODUCT, CATALOG_PRODUCT_MODIFIER_OPTION } = orderLineItemEntities
      
      return !!this.orderLines.filter(({ id, itemEntity, price }) => {
        if (itemEntity === PRODUCT) {
          const modifiers = this.orderLines.filter(({ itemEntity, parentOrderLine, includedOnParentLine }) => {
            return itemEntity === CATALOG_PRODUCT_MODIFIER_OPTION && parentOrderLine === id && !includedOnParentLine
          })

          const totalPriceModifiers = modifiers.reduce((total, modifier) => {
            return total + modifier.price[0].amount
          }, 0)

          return !(price[0].amount + totalPriceModifiers)
        }

        return false
      }).length
    },
    serviceChargesOrderLines () {
      const serviceCharges = this.orderLines.filter((orderLine) => orderLine.orderLineType === orderLineTypes.SERVICE_CHARGE && orderLine.price[0].amount)
      return serviceCharges
    },
    serviceChargesToShow () {
      const setOfValues = new Set()
      const valuesOfServiceCharges = {}
      for (let serviceCharge of this.serviceChargesOrderLines) {
        const { value, quantity, price } = serviceCharge
        const { amount, currency } = price[0]
        if (setOfValues.has(value)) {
          valuesOfServiceCharges[value].amount += amount * quantity
        }
        else {
          setOfValues.add(value)
          valuesOfServiceCharges[value] = {
            amount: amount * quantity,
            currency,
            value,
          }
        }
      }
      return Object.values(valuesOfServiceCharges)
    },
    totalPrice () {
      const { amount: subtotal, currency } = this.totalByCurrencies[0]
      const total = this.serviceChargesToShow.reduce(
        (acc, serviceCharge) => acc + serviceCharge.amount,
        subtotal
      )
      return { amount: total, currency }
    },
    serviceChargesOrderLines () {
      const serviceCharges = this.orderLines.filter((orderLine) => orderLine.orderLineType === orderLineTypes.SERVICE_CHARGE && orderLine.price[0].amount)
      return serviceCharges
    },
    serviceChargesToShow () {
      const setOfValues = new Set()
      const valuesOfServiceCharges = {}
      for (let serviceCharge of this.serviceChargesOrderLines) {
        const { value, quantity, price } = serviceCharge
        const { amount, currency } = price[0]
        if (setOfValues.has(value)) {
          valuesOfServiceCharges[value].amount += amount * quantity
        }
        else {
          setOfValues.add(value)
          valuesOfServiceCharges[value] = {
            amount: amount * quantity,
            currency,
            value,
          }
        }
      }
      return Object.values(valuesOfServiceCharges)
    },
  },
}
</script>

<style lang="scss" scoped>
.bold-text {
  font-weight: 500 !important;
}
.fix-height {
  height: 15px;
}
.currency-text {
  font-size: 16px;
}
.amount-text {
  font-size: 20px;
}
.totals-text {
  font-size: 20px;
}
.service-charge-text {
  font-size: 12px;
}
.color-text {
  color: rgba(0, 0, 0, 0.6);
}
::v-deep {
  .free-price-alert {
    font-weight: 500 !important;
    color: rgba(0, 0, 0, 0.6);
  }
}
</style>
